import React, { useState, useRef, useEffect } from "react";
import {
	Upload,
	FileText,
	Download,
	Trash2,
	CheckCircle,
	Loader2,
	AlertCircle,
	Eye,
	X,
	ArrowLeft,
	XCircle,
	Clock,
} from "lucide-react";
import { MainLayout } from "./MainLayout";
import { useTranslation } from "../i18n/TranslationContext";
import { useWorkPermitProof } from "../api/workPermitProof/useWorkPermitProof";
import { useCurrentUser } from "../api/auth/useCurrentUser";
import { useAppRouter } from "../hooks/useAppRouter";
import { showError } from "../services/notification.service";
import { useGetExtra } from "../api/extras/useGetExtra";
import { useGetCountries } from "../api/countries/useGetCountries";

export function WorkPermitProof() {
	const router = useAppRouter();
	const { t } = useTranslation();
	const { user } = useCurrentUser();
	const fileInputRef = useRef<HTMLInputElement>(null);

	// Get user's nationality
	const { extra } = useGetExtra(user?.extraId);
	const { countries } = useGetCountries();
	const userCountry = countries.find(
		(country) => country.country_id === extra?.nationality
	);

	const {
		proofInfo,
		loading,
		uploading,
		uploadProof,
		downloadProof,
		deleteProof,
		refetch,
	} = useWorkPermitProof(user?.extraId);

	const [selectedFile, setSelectedFile] = useState<File | null>(null);
	const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
	const [showPreview, setShowPreview] = useState(false);
	const [previewUrl, setPreviewUrl] = useState<string | null>(null);
	const [previewLoading, setPreviewLoading] = useState(false);

	const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
		const file = event.target.files?.[0];
		if (file) {
			// Validate file size (10MB max)
			if (file.size > 10 * 1024 * 1024) {
				showError("Die Datei ist zu groß. Maximale Größe: 10MB");
				return;
			}
			setSelectedFile(file);
		}
	};

	const handleUpload = async () => {
		if (!selectedFile) return;

		const success = await uploadProof(selectedFile);
		if (success) {
			setSelectedFile(null);
			if (fileInputRef.current) {
				fileInputRef.current.value = "";
			}
			// Refetch proof info to ensure we have the latest data including mime_type
			await refetch();
		}
	};

	const handleDelete = async () => {
		const success = await deleteProof();
		if (success) {
			setShowDeleteConfirm(false);
		}
	};

	const formatFileSize = (bytes: number) => {
		if (bytes === 0) return "0 Bytes";
		const k = 1024;
		const sizes = ["Bytes", "KB", "MB", "GB"];
		const i = Math.floor(Math.log(bytes) / Math.log(k));
		return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
	};

	const formatDate = (dateString: string) => {
		return new Date(dateString).toLocaleDateString("de-DE", {
			day: "2-digit",
			month: "2-digit",
			year: "numeric",
			hour: "2-digit",
			minute: "2-digit",
		});
	};

	const handleBack = () => {
		router.replace("app-profile-completion");
	};

	const handlePreview = async () => {
		if (!user?.extraId || !proofInfo) return;

		setPreviewLoading(true);
		try {
			const response = await fetch(
				`${import.meta.env.VITE_API_URL}/extras/${user.extraId}/work-permit-proof`,
				{
					method: "GET",
					headers: {
						Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
					},
				}
			);

			if (!response.ok) {
				throw new Error("Failed to load file");
			}

			const blob = await response.blob();
			const url = window.URL.createObjectURL(blob);
			setPreviewUrl(url);
			setShowPreview(true);
		} catch (error) {
			console.error("Error loading preview:", error);
			showError("Fehler beim Laden der Vorschau");
		} finally {
			setPreviewLoading(false);
		}
	};

	const closePreview = () => {
		if (previewUrl) {
			window.URL.revokeObjectURL(previewUrl);
			setPreviewUrl(null);
		}
		setShowPreview(false);
	};

	const isPreviewable = (mimeType: string | null) => {
		if (!mimeType) return false;
		return mimeType.startsWith("image/") || mimeType === "application/pdf";
	};

	if (loading) {
		return (
			<MainLayout showBack={true} onBack={handleBack}>
				<div className="flex items-center justify-center min-h-[400px]">
					<Loader2 className="w-8 h-8 animate-spin text-[var(--ucm-primary-teal)]" />
				</div>
			</MainLayout>
		);
	}

	if (userCountry?.is_eu) {
		return (
			<MainLayout showBack={true} onBack={handleBack}>
				<div className="work-permit-proof-screen">
					<h1 className="text-2xl font-bold text-[var(--ucm-text-dark)] mb-3">
						Arbeitserlaubnis
					</h1>

					<p className="text-[var(--ucm-text-medium)] mb-6">
						Als EU-Bürger benötigen Sie keine Arbeitserlaubnis.
					</p>
				</div>

				{/* Country Info */}
				{userCountry && (
					<div className="bg-[var(--ucm-info-blue-light)] border border-[var(--ucm-info-blue)] rounded-2xl p-4 mb-6">
						<div className="flex items-start space-x-3">
							<AlertCircle className="w-5 h-5 text-[var(--ucm-info-blue)] mt-0.5 flex-shrink-0" />
							<div>
								<p className="font-medium text-[var(--ucm-text-dark)] mb-1">
									Ihre Staatsangehörigkeit: {userCountry.nationality}
								</p>
								<p className="text-sm text-[var(--ucm-text-dark)]">
									{userCountry.is_eu
										? "Als EU-Bürger benötigen Sie keine Arbeitserlaubnis."
										: "Bitte laden Sie Ihre gültige Arbeitserlaubnis für Deutschland hoch."}
								</p>
							</div>
						</div>
					</div>
				)}
			</MainLayout>
		);
	}

	return (
		<MainLayout showBack={true} onBack={handleBack}>
			<div className="work-permit-proof-screen">
				{/* Header */}
				<div className="mb-6">
					<h1 className="text-2xl font-bold text-[var(--ucm-text-dark)] mb-3">
						Arbeitserlaubnis
					</h1>
					<p className="text-[var(--ucm-text-medium)]">
						Als Nicht-EU-Bürger benötigen wir eine gültige Arbeitserlaubnis für
						Deutschland.
					</p>
				</div>

				{/* Country Info */}
				{userCountry && (
					<div className="bg-[var(--ucm-info-blue-light)] border border-[var(--ucm-info-blue)] rounded-2xl p-4 mb-6">
						<div className="flex items-start space-x-3">
							<AlertCircle className="w-5 h-5 text-[var(--ucm-info-blue)] mt-0.5 flex-shrink-0" />
							<div>
								<p className="font-medium text-[var(--ucm-text-dark)] mb-1">
									Ihre Staatsangehörigkeit: {userCountry.nationality}
								</p>
								<p className="text-sm text-[var(--ucm-text-dark)]">
									{userCountry.is_eu
										? "Als EU-Bürger benötigen Sie keine Arbeitserlaubnis."
										: "Bitte laden Sie Ihre gültige Arbeitserlaubnis für Deutschland hoch."}
								</p>
							</div>
						</div>
					</div>
				)}

				{/* Info Box */}
				<div className="bg-[var(--ucm-light-blue)] border border-[var(--ucm-primary-teal)] rounded-2xl p-4 mb-6">
					<div className="flex items-start space-x-3">
						<AlertCircle className="w-5 h-5 text-[var(--ucm-primary-teal)] mt-0.5" />
						<div className="text-sm text-[var(--ucm-text-dark)]">
							<p className="mb-2">
								Akzeptierte Dateiformate: PDF, JPG, PNG, GIF, DOC, DOCX
							</p>
							<p>Maximale Dateigröße: 10 MB</p>
						</div>
					</div>
				</div>

				{/* Current Proof Info */}
				{proofInfo && (
					<div className="bg-white rounded-2xl p-6 shadow-[0_4px_16px_rgba(0,0,0,0.08)] mb-6 relative">
						{/* Status Badge */}
						{proofInfo.status && (
							<div
								className={`absolute top-4 right-4 px-3 py-1 rounded-full text-xs font-medium ${
									proofInfo.status === "pending"
										? "bg-yellow-100 text-yellow-800"
										: proofInfo.status === "approved"
											? "bg-green-100 text-green-800"
											: "bg-red-100 text-red-800"
								}`}
							>
								<div className="flex items-center gap-1">
									{proofInfo.status === "approved" && (
										<CheckCircle className="w-5 h-5 text-green-800" />
									)}

									{proofInfo.status === "pending" && (
										<Clock className="w-5 h-5 text-yellow-800" />
									)}
									{proofInfo.status === "rejected" && (
										<XCircle className="w-5 h-5 text-red-800" />
									)}

									{proofInfo.status === "pending" && "Ausstehend"}
									{proofInfo.status === "approved" && "Genehmigt"}
									{proofInfo.status === "rejected" && "Abgelehnt"}
								</div>
							</div>
						)}

						<div className="flex items-center justify-between mb-4">
							<h2 className="font-semibold text-[var(--ucm-text-dark)]">
								Aktuelle Arbeitserlaubnis
							</h2>
							{proofInfo.status === "approved" && (
								<CheckCircle className="w-5 h-5 text-[var(--ucm-success-green)]" />
							)}
						</div>

						<div className="space-y-3">
							<div className="flex items-center justify-between py-2">
								<div className="flex items-center space-x-3">
									<FileText className="w-5 h-5 text-[var(--ucm-primary-teal)]" />
									<div>
										<p className="font-medium text-[var(--ucm-text-dark)]">
											{proofInfo.file_name}
										</p>
										<p className="text-sm text-[var(--ucm-text-medium)]">
											{formatFileSize(proofInfo.file_size)} •{" "}
											{formatDate(proofInfo.uploaded_at)}
										</p>
									</div>
								</div>
							</div>

							{/* Rejection Reason Display */}
							{proofInfo.status === "rejected" && proofInfo.rejection_reason && (
								<div className="bg-[#fef2f2] border border-[#fecaca] rounded-xl p-4">
									<div className="flex items-start space-x-3">
										<XCircle className="w-5 h-5 text-[#dc2626] mt-0.5 flex-shrink-0" />
										<div>
											<p className="font-medium text-[#dc2626] mb-2">
												Ablehnungsgrund
											</p>
											<p className="text-sm text-[#7f1d1d] leading-relaxed">
												{proofInfo.rejection_reason}
											</p>
										</div>
									</div>
								</div>
							)}

							<div className="flex gap-3 pt-3">
								{isPreviewable(proofInfo.mime_type) && (
									<button
										onClick={handlePreview}
										disabled={previewLoading}
										className="flex-1 flex items-center justify-center gap-2 h-10 bg-[var(--ucm-info-blue)] text-white rounded-xl hover:opacity-90 transition-all disabled:bg-[var(--ucm-disabled-gray)] disabled:cursor-not-allowed"
									>
										{previewLoading ? (
											<Loader2 className="w-4 h-4 animate-spin" />
										) : (
											<>
												<Eye className="w-4 h-4" />
												Vorschau
											</>
										)}
									</button>
								)}
								<button
									onClick={downloadProof}
									className="flex-1 flex items-center justify-center gap-2 h-10 bg-[var(--ucm-primary-teal)] text-white rounded-xl hover:bg-[var(--ucm-primary-teal-hover)] transition-colors"
								>
									<Download className="w-4 h-4" />
									Herunterladen
								</button>
								<button
									onClick={() => setShowDeleteConfirm(true)}
									disabled={proofInfo.status === "approved"}
									className={`flex-1 flex items-center justify-center gap-2 h-10 rounded-xl transition-colors ${
										proofInfo.status === "approved"
											? "border border-gray-300 text-gray-400 cursor-not-allowed"
											: "border border-[var(--ucm-error-red)] text-[var(--ucm-error-red)] hover:bg-[var(--ucm-error-red)] hover:text-white"
									}`}
									title={
										proofInfo.status === "approved"
											? "Genehmigte Dokumente können nicht gelöscht werden"
											: ""
									}
								>
									<Trash2 className="w-4 h-4" />
									Löschen
								</button>
							</div>
						</div>
					</div>
				)}

				{/* Delete Confirmation Modal */}
				{showDeleteConfirm && (
					<div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
						<div className="bg-white rounded-2xl max-w-md w-full p-6">
							<div className="flex items-center justify-between mb-4">
								<h3 className="text-lg font-semibold text-[var(--ucm-text-dark)]">
									Löschen bestätigen
								</h3>
								<button
									onClick={() => setShowDeleteConfirm(false)}
									className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
								>
									<X className="w-5 h-5 text-[var(--ucm-text-medium)]" />
								</button>
							</div>

							<p className="text-[var(--ucm-text-medium)] mb-6">
								Möchten Sie diese Arbeitserlaubnis wirklich löschen?
							</p>

							<div className="flex gap-3">
								<button
									onClick={handleDelete}
									className="flex-1 h-10 bg-[var(--ucm-error-red)] text-white rounded-xl hover:opacity-90 transition-opacity"
								>
									Ja, löschen
								</button>
								<button
									onClick={() => setShowDeleteConfirm(false)}
									className="flex-1 h-10 border border-[var(--ucm-border-medium)] text-[var(--ucm-text-dark)] rounded-xl hover:bg-gray-50 transition-colors"
								>
									Abbrechen
								</button>
							</div>
						</div>
					</div>
				)}

				{/* Upload Section */}
				{!proofInfo && (
					<div className="bg-white rounded-2xl p-6 shadow-[0_4px_16px_rgba(0,0,0,0.08)]">
						<h2 className="font-semibold text-[var(--ucm-text-dark)] mb-4">
							{proofInfo
								? "Neue Arbeitserlaubnis hochladen"
								: "Arbeitserlaubnis hochladen"}
						</h2>

						<input
							ref={fileInputRef}
							type="file"
							accept=".pdf,.jpg,.jpeg,.png,.gif,.doc,.docx"
							onChange={handleFileSelect}
							className="hidden"
						/>

						{!selectedFile ? (
							<button
								onClick={() => fileInputRef.current?.click()}
								className="w-full border-2 border-dashed border-[var(--ucm-border-medium)] rounded-xl p-8 hover:border-[var(--ucm-primary-teal)] transition-colors"
							>
								<Upload className="w-12 h-12 text-[var(--ucm-text-medium)] mx-auto mb-3" />
								<p className="text-[var(--ucm-text-medium)]">
									Klicken Sie hier, um eine Datei auszuwählen
								</p>
							</button>
						) : (
							<div>
								<div className="bg-[var(--ucm-light-gray)] rounded-xl p-4 mb-4">
									<div className="flex items-center justify-between">
										<div className="flex items-center space-x-3">
											<FileText className="w-5 h-5 text-[var(--ucm-primary-teal)]" />
											<div>
												<p className="font-medium text-[var(--ucm-text-dark)]">
													{selectedFile.name}
												</p>
												<p className="text-sm text-[var(--ucm-text-medium)]">
													{formatFileSize(selectedFile.size)}
												</p>
											</div>
										</div>
										<button
											onClick={() => {
												setSelectedFile(null);
												if (fileInputRef.current) {
													fileInputRef.current.value = "";
												}
											}}
											className="text-[var(--ucm-text-medium)] hover:text-[var(--ucm-text-dark)] rounded-full bg-[var(--ucm-light-red)] p-2"
										>
											<Trash2 className="w-4 h-4  text-[var(--ucm-error-red)]" />
										</button>
									</div>
								</div>

								<div className="flex gap-3">
									<button
										onClick={handleUpload}
										disabled={uploading}
										className={`flex-1 h-12 font-semibold rounded-xl transition-colors flex items-center justify-center ${
											!uploading
												? "bg-[var(--ucm-primary-teal)] text-white hover:bg-[var(--ucm-primary-teal-hover)]"
												: "bg-[var(--ucm-disabled-gray)] text-[var(--ucm-text-medium)] cursor-not-allowed"
										}`}
									>
										{uploading ? (
											<>
												<Loader2 className="w-5 h-5 animate-spin mr-2" />
												Wird hochgeladen...
											</>
										) : (
											<>
												<Upload className="w-5 h-5 mr-2" />
												Hochladen
											</>
										)}
									</button>
									<button
										onClick={() => fileInputRef.current?.click()}
										disabled={uploading}
										className="h-12 px-6 border border-[var(--ucm-border-medium)] text-[var(--ucm-text-dark)] rounded-xl hover:bg-gray-50 transition-colors"
									>
										Andere Datei wählen
									</button>
								</div>
							</div>
						)}
					</div>
				)}

				{/* Preview Modal */}
				{showPreview && previewUrl && (
					<div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
						<div className="bg-white rounded-2xl max-w-4xl max-h-[90vh] w-full overflow-hidden flex flex-col">
							{/* Modal Header */}
							<div className="flex items-center justify-between p-4 border-b border-[var(--ucm-border-light)]">
								<h3 className="text-lg font-semibold text-[var(--ucm-text-dark)]">
									{proofInfo?.file_name}
								</h3>
								<button
									onClick={closePreview}
									className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
								>
									<X className="w-5 h-5 text-[var(--ucm-text-medium)]" />
								</button>
							</div>

							{/* Modal Content */}
							<div className="flex-1 overflow-auto p-4">
								{proofInfo?.mime_type?.startsWith("image/") ? (
									<img
										src={previewUrl}
										alt={proofInfo.file_name}
										className="max-w-full h-auto mx-auto"
									/>
								) : proofInfo?.mime_type === "application/pdf" ? (
									<iframe
										src={previewUrl}
										className="w-full h-full min-h-[600px]"
										title={proofInfo.file_name}
									/>
								) : (
									<div className="text-center py-8">
										<FileText className="w-16 h-16 text-[var(--ucm-text-medium)] mx-auto mb-4" />
										<p className="text-[var(--ucm-text-medium)]">
											Vorschau nicht verfügbar
										</p>
									</div>
								)}
							</div>

							{/* Modal Footer */}
							<div className="flex gap-3 p-4 border-t border-[var(--ucm-border-light)]">
								<button
									onClick={downloadProof}
									className="flex-1 flex items-center justify-center gap-2 h-10 bg-[var(--ucm-primary-teal)] text-white rounded-xl hover:bg-[var(--ucm-primary-teal-hover)] transition-colors"
								>
									<Download className="w-4 h-4" />
									Herunterladen
								</button>
								<button
									onClick={closePreview}
									className="flex-1 h-10 border border-[var(--ucm-border-medium)] text-[var(--ucm-text-dark)] rounded-xl hover:bg-gray-50 transition-colors"
								>
									Schließen
								</button>
							</div>
						</div>
					</div>
				)}
			</div>
		</MainLayout>
	);
}
